import type { StockPoint } from '@jums-dashboard/shared';
import { CategoryScale, Chart as ChartJS, Filler, LineElement, LinearScale, PointElement } from 'chart.js';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import type { UpDown } from './stocksDerive';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler);

const COLOR_VAR: Record<UpDown, string> = {
  up: '--accent-success',
  down: '--accent-danger',
  flat: '--text-muted',
};

const FALLBACK: Record<UpDown, string> = {
  up: '#22c55e',
  down: '#ef4444',
  flat: '#94a3b8',
};

interface StocksSparklineProps {
  points: StockPoint[];
  sign: UpDown;
  className?: string;
}

/** Tiny axis-less area chart colored by day-change sign. Decorative (aria-hidden). */
export default function StocksSparkline({ points, sign, className }: StocksSparklineProps) {
  const color = useMemo(() => {
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(COLOR_VAR[sign]).trim();
    return resolved || FALLBACK[sign];
  }, [sign]);

  const data = useMemo(
    () => ({
      labels: points.map((_, i) => i),
      datasets: [
        {
          data: points.map((p) => p.close),
          borderColor: color,
          backgroundColor: `${color}22`,
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.35,
          fill: true,
          spanGaps: true,
        },
      ],
    }),
    [points, color],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false } },
    }),
    [],
  );

  if (points.length < 2) return null;

  return (
    <div className={className} aria-hidden="true">
      <Line data={data} options={options} />
    </div>
  );
}
